import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type MarketSettingsCentralMappingHierarchyController from 'lumen/controllers/market/settings/central-mapping/hierarchy';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';
import type { Breakdown } from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSettingsCentralMappingHierarchyRoute extends Route {
  queryParams = {
    breakdown: {
      refreshModel: true,
    },
  };

  @service declare centralMapping: Services['central-mapping'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @tracked transitionHandler: ((transition: Transition) => void) | null = null;

  async beforeModel(transition: TransitionWithTo): Promise<void> {
    const { getBreakdownsTask } = this.centralMapping;

    // Await the breakdowns task to ensure the available breakdowns are loaded before validating the query params.
    await getBreakdownsTask.last;

    this.transition.validateCentralMappingQueryParams(
      transition,
      this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
    );

    // We need to clean up in the beforeModel hook instead of the deactivate hook.
    // Since some tasks are shared between the hierarchy and the mapping routes,
    // the deactivate hook of the previous route might be called after the model hook of the next route.
    // In that case, the task would immediately be cancelled, resulting in no data being loaded after transitioning.
    this.cleanup();
  }

  cleanup(): void {
    void this.centralMapping.getHierarchyTask.cancelAll();

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('central-mapping/category');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: { breakdown: Breakdown }) {
    const model = {
      hierarchy: this.centralMapping.getHierarchyTask,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.hierarchy.perform(params.breakdown);
    return model;
  }

  resetController(
    controller: MarketSettingsCentralMappingHierarchyController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    if (this.transitionHandler) {
      controller.router.off('routeWillChange', this.transitionHandler);
    }

    // Reset the selected categories, otherwise some categories can still be selected when the user navigates back to the route.
    // This is not a problem in itself, but when navigating back to the route for a different breakdown, some previously selected categories
    // might be visible that should not be visible for the new breakdown.
    controller.selectedCategories = [];

    super.resetController(controller, isExiting, transition);
  }

  setupController(
    controller: MarketSettingsCentralMappingHierarchyController,
    model: ModelFrom<MarketSettingsCentralMappingHierarchyRoute>,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-misused-promises
    this.transitionHandler = (t: Transition) =>
      this.centralMapping.onRouteWillChange(
        this.routeName,
        controller,
        t as TransitionWithTo,
      );
    controller.router.on('routeWillChange', this.transitionHandler);
  }
}
